import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { de as deLocale, enUS } from "date-fns/locale";
import { Check, Pencil, Trash2, Calendar as CalendarIcon, Clock, Bell, Timer as TimerIcon, Flag, Repeat, StickyNote, ListChecks, Plus, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EditTaskDialog } from "@/components/todo/EditTaskDialog";
import { useApp } from "@/context/AppContext";
import { getCategory, getPriority } from "@/lib/translations";

export const TaskItem = ({ task, onToggle, onDelete, onEdit, onStartTimer, onUpdate }) => {
  const { t, lang } = useApp();
  const [editing, setEditing] = useState(false);
  const [newItem, setNewItem] = useState("");
  const locale = lang === "de" ? deLocale : enUS;

  const checklist = task.checklist || [];
  const doneCount = checklist.filter((i) => i.done).length;

  const patchChecklist = (list) => onUpdate && onUpdate(task.id, { checklist: list });
  const toggleItem = (id) =>
    patchChecklist(checklist.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  const removeItem = (id) => patchChecklist(checklist.filter((i) => i.id !== id));
  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    patchChecklist([...checklist, { id: crypto.randomUUID(), text, done: false }]);
    setNewItem("");
  };

  const category = getCategory(task.category);
  const priority = getPriority(task.priority);
  const repeatKey = task.repeat && task.repeat !== "none" ? task.repeat : null;
  const created = task.created_at ? format(new Date(task.created_at), "dd MMM yyyy", { locale }) : "";
  const due = task.due_date ? format(new Date(task.due_date), "dd MMM, HH:mm", { locale }) : null;
  const reminder = task.reminder_at ? format(new Date(task.reminder_at), "dd MMM, HH:mm", { locale }) : null;
  const overdue = task.due_date && !task.completed && new Date(task.due_date).getTime() < Date.now();
  const repeatLabelKey = {
    daily: "repeatDaily",
    weekdays: "repeatWeekdays",
    weekly: "repeatWeekly",
    monthly: "repeatMonthly",
  }[repeatKey];

  return (
    <motion.li
      layout
      data-testid={`task-item-${task.id}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 80, transition: { duration: 0.2 } }}
      transition={{ duration: 0.25 }}
      className={`group flex items-start gap-3 p-4 rounded-xl border border-border bg-card transition-all hover:shadow-md hover:border-primary/30 border-l-4 ${
        task.completed ? "opacity-70" : overdue ? "bg-destructive/5 border-destructive/30" : ""
      }`}
      style={{ borderLeftColor: task.completed ? "hsl(var(--border))" : priority.color }}
    >
      <button
        data-testid={`task-checkbox-${task.id}`}
        onClick={() => onToggle(task)}
        className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
          task.completed
            ? "bg-success border-success text-white"
            : "border-muted-foreground/40 hover:border-primary"
        }`}
        aria-label="toggle"
      >
        {task.completed && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500 }}>
            <Check className="w-4 h-4" strokeWidth={3} />
          </motion.span>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <p
          data-testid={`task-title-${task.id}`}
          className={`text-base font-medium break-words transition-colors ${
            task.completed ? "line-through text-muted-foreground" : "text-foreground"
          }`}
        >
          {task.title}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
          <span
            data-testid={`task-priority-${task.id}`}
            className="inline-flex items-center gap-1 font-medium"
            style={{ color: priority.color }}
          >
            <Flag className="w-3 h-3" /> {t(priority.labelKey)}
          </span>
          {category && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${category.color}1a`, color: category.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: category.color }} />
              {t(category.labelKey)}
            </span>
          )}
          {repeatLabelKey && (
            <span className="inline-flex items-center gap-1" data-testid={`task-repeat-${task.id}`}>
              <Repeat className="w-3 h-3" /> {t(repeatLabelKey)}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {t("createdOn")} {created}
          </span>
          {due && (
            <span
              data-testid={`task-due-${task.id}`}
              className={`inline-flex items-center gap-1 font-medium ${overdue ? "text-destructive" : "text-warning"}`}
            >
              <CalendarIcon className="w-3 h-3" /> {due}
              {overdue && <span className="ml-0.5 font-semibold">· {t("overdue")}</span>}
            </span>
          )}
          {reminder && (
            <span className="inline-flex items-center gap-1 text-primary font-medium">
              <Bell className="w-3 h-3" /> {reminder}
            </span>
          )}
        </div>

        {task.notes && (
          <p
            data-testid={`task-notes-${task.id}`}
            className="mt-2 text-sm text-muted-foreground whitespace-pre-line flex gap-1.5"
          >
            <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="break-words">{task.notes}</span>
          </p>
        )}

        {(checklist.length > 0 || onUpdate) && (
          <div className="mt-2.5" data-testid={`task-checklist-${task.id}`}>
            {checklist.length > 0 && (
              <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-muted-foreground">
                <ListChecks className="w-3.5 h-3.5" />
                <span>{doneCount}/{checklist.length}</span>
              </div>
            )}
            <div className="space-y-1">
              {checklist.map((item) => (
                <div key={item.id} className="flex items-center gap-2 group/ci">
                  <button
                    data-testid={`checklist-toggle-${item.id}`}
                    onClick={() => toggleItem(item.id)}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      item.done ? "bg-success border-success text-white" : "border-muted-foreground/40 hover:border-primary"
                    }`}
                  >
                    {item.done && <Check className="w-2.5 h-2.5" strokeWidth={4} />}
                  </button>
                  <span className={`text-sm ${item.done ? "line-through text-muted-foreground" : "text-foreground/90"}`}>
                    {item.text}
                  </span>
                  {onUpdate && (
                    <button
                      data-testid={`checklist-remove-${item.id}`}
                      onClick={() => removeItem(item.id)}
                      className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover/ci:opacity-100 hover:text-destructive transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {onUpdate && !task.completed && (
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  data-testid={`checklist-input-${task.id}`}
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addItem();
                    }
                  }}
                  placeholder={t("addItem")}
                  className="flex-1 h-8 bg-background border border-border rounded-lg px-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                />
                <button
                  data-testid={`checklist-add-${task.id}`}
                  onClick={addItem}
                  className="h-8 w-8 shrink-0 rounded-lg bg-secondary text-foreground flex items-center justify-center hover:bg-accent transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {!task.completed && onStartTimer && (
          <button
            data-testid={`timer-task-btn-${task.id}`}
            onClick={() => onStartTimer(task)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            aria-label={t("startTimerForTask")}
          >
            <TimerIcon className="w-4 h-4" />
          </button>
        )}
        <button
          data-testid={`edit-task-btn-${task.id}`}
          onClick={() => setEditing(true)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          aria-label={t("edit")}
        >
          <Pencil className="w-4 h-4" />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid={`delete-task-btn-${task.id}`}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              aria-label={t("delete")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("confirmDeleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("confirmDeleteDesc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-delete"
                onClick={() => onDelete(task)}
                className="bg-destructive text-destructive-foreground hover:brightness-110"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <EditTaskDialog open={editing} onOpenChange={setEditing} task={task} onSave={onEdit} />
    </motion.li>
  );
};
